import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { mailSessionKey, readMailSession, writeMailSession, deleteMailSession, sortedMailHistory, reusableSentState, reusableDraftState, type MailComposeSession, type MailDraftState, type MailHistoryPage } from "@/lib/mail-compose-session";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { Send, X, Paperclip, Plus, Trash2, Users, Reply, Forward, GripVertical, UserPlus, Clock, AlertTriangle } from "lucide-react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { PulseSequenceTags } from "@/components/pulse-sequence-tags";
import { neutralizeCidImagesHtml } from "@/lib/pulse-link";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { getShareEmailGreeting, getShareEmailSendFields, updateShareEmailGreeting } from "@/lib/share-email-draft";
import { buildForwardSubject } from "@/lib/email-subject";
import { isValidSequenceId } from "@/lib/sequence-utils";
import type { ShareEmailReadiness, ShareEmailSendMode } from "@shared/share-email-delivery";
import type { ShareEmailOverview, EmailThumbnailMode } from "@shared/share-email-overview";
import { ShareEmailOverviewEditor } from "@/components/share-email-overview";

interface MailOutDialogBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  initialDraft?: {
    key: string;
    subject: string;
    htmlContent: string;
    shareGreeting?: boolean;
    /** Set when this email delivers a Preview/Final share link — the server
     *  stamps the resulting email_out pulse so it links back to the share. */
    previewShareId?: number;
    shareUrl?: string;
  };
  composeLabel?: string;
}

type MailOutDialogProps = MailOutDialogBaseProps & (
  {
  /**
   * Opens the composer against the source email linked to this incoming pulse.
   * The source itself is resolved server-side so the cache record never has to
   * be guessed from display data.
   */
  replySource?: {
    pulseId: number;
  };
    forwardSource?: never;
  }
  | {
  /**
   * Mutually exclusive with replySource. A forwarded message starts a new
   * conversation, but keeps an authoritative original snapshot server-side.
   */
  forwardSource?: {
    pulseId: number;
  };
    replySource?: never;
  }
);

/**
 * GET /api/pulses/:pulseId/reply-source
 * The response deliberately contains the cache id used by POST /api/emails/send,
 * plus the immutable source snapshot used by the read-only mail pane.
 */
interface PulseReplySource {
  emailId: number;
  subject: string | null;
  fromName: string | null;
  fromAddress: string;
  emailDate: string | null;
  emailBody: string | null;
  emailAccountId: string;
}

interface ForwardSourceAttachment {
  id: string;
  fileName: string;
  contentType: string;
  size: number | null;
  available: boolean;
  unavailableReason?: string;
}

interface PulseForwardSource extends PulseReplySource {
  attachments: ForwardSourceAttachment[];
  toAddresses?: string[];
  ccAddresses?: string[];
}

interface EmailRecipient {
  email: string;
  name?: string;
  type: 'to' | 'cc' | 'bcc';
}

interface AttachmentFile {
  file: File;
  name: string;
  size: number;
  preview?: string;
}

interface SuggestedRecipient {
  email: string;
  name: string | null;
  firstName?: string | null;
  contactId: string | null;
  clientId: string | null;
  category: string | null;
  role: string | null;
  isInternal: boolean;
  score: number;
  lastSeen: string | null;
  source?: 'project_history' | 'project_contact' | 'client_history' | 'client_contact' | 'internal';
}

interface RecipientGroup {
  kind: 'last_email_out' | 'incoming';
  sourceEmailId: number | null;
  subject: string | null;
  date: string | null;
  members: Array<{ email: string; name: string | null; type: 'to' | 'cc' }>;
}

interface SuggestionsResponse {
  suggestions: SuggestedRecipient[];
  groups: RecipientGroup[];
  internalEmails?: string[];
  internalDomains?: string[];
}

interface EmailSendOption {
  id: string;
  name: string;
  email: string;
  isPrimary: boolean;
  /** 'own' = the caller's personal address, 'shared' = team pool account. */
  ownership: 'own' | 'shared';
}

function newDeliveryRequestId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Older browsers and test environments use the fallback below.
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.random() * 16 | 0;
    const value = character === "x" ? random : (random & 0x3 | 0x8);
    return value.toString(16);
  });
}

export function appendShareSequenceFields(formData: FormData, selectedSequences: readonly string[]): string[] {
  // PulseSequenceTags already supplies canonical schema tokens (MST, 01-99,
  // or A-Z). Preserve those tokens verbatim: Number("MST")/Number("A") would
  // silently drop valid project sequence tags.
  const sequenceNumbers = selectedSequences
    .map((value) => value.trim())
    .filter(isValidSequenceId);
  if (sequenceNumbers.length > 0) {
    // Keep the original scalar for older send consumers while the array
    // preserves every selected sequence for durable share delivery.
    formData.append('sequenceNumber', String(sequenceNumbers[0]));
    formData.append('sequenceNumbers', JSON.stringify(sequenceNumbers));
  }
  return sequenceNumbers;
}

const CONTACT_CATEGORIES = ['Freelancer', 'Partner', 'Client Division', 'Supplier', 'Other'];
const FORWARD_COMBINED_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
const FORWARD_COMBINED_ATTACHMENT_MAX_COUNT = 20;
const PROJECT_ROLES: Array<{ value: string; label: string }> = [
  { value: 'main_contact', label: 'Main contact' },
  { value: 'c_level', label: 'C-level' },
  { value: 'other', label: 'Other' },
];

function roleLabel(role: string | null): string | null {
  if (!role) return null;
  return PROJECT_ROLES.find(r => r.value === role)?.label ?? role;
}

/** Display name for an email account — the provider suffix is an internal detail. */
function accountDisplayName(name: string | null | undefined): string {
  return (name || '').replace(/\s*\(\s*hosteurope\s*\)\s*/gi, ' ').replace(/\s+/g, ' ').trim();
}

/** Client-side mirror for display; the send route enforces this again. */
function ensureSingleProjectId(subject: string, projectId: string): string {
  const pid = projectId.toUpperCase();
  const escaped = pid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  let cleaned = (subject || '').trim()
    .replace(new RegExp(`(?:\\(\\s*${escaped}\\s*\\)|\\[\\s*${escaped}\\s*\\])`, 'gi'), ' ')
    .replace(new RegExp(`(^|[^A-Z0-9])${escaped}(?=$|[^A-Z0-9])`, 'gi'), '$1')
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return cleaned ? `${cleaned} (${pid})` : `(${pid})`;
}

function buildReplySubject(sourceSubject: string | null, projectId: string): string {
  const source = (sourceSubject || 'Email').trim();
  const withReplyPrefix = /^re\s*:/i.test(source) ? source : `Re: ${source}`;
  return ensureSingleProjectId(withReplyPrefix, projectId);
}

function renderOriginalEmailBody(body: string | null): string {
  const value = (body || '').trim();
  if (!value) return '<p>Kein Originalinhalt gespeichert.</p>';
  const looksLikeHtml = /<\/?[a-z][a-z0-9:]*(?:\s[^>]*)?>/i.test(value);
  if (looksLikeHtml) return sanitizeHtml(neutralizeCidImagesHtml(value));
  const escaped = value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r?\n/g, '<br>');
  return escaped;
}

export function MailOutDialog(props: MailOutDialogProps) {
  const { projectId, initialDraft, replySource, forwardSource } = props;
  const { user } = useAuth();
  const contextKey = forwardSource ? `forward:${forwardSource.pulseId}`
    : replySource ? `reply:${replySource.pulseId}` : initialDraft ? `share:${initialDraft.key}` : 'normal';
  const sessionKey = mailSessionKey(String(user?.id ?? 'anonymous'), projectId, contextKey);
  return <MailOutSession key={sessionKey} {...props} sessionKey={sessionKey} contextKey={contextKey} />;
}

function MailOutSession({ open, onOpenChange, projectId, initialDraft, composeLabel, replySource, forwardSource, sessionKey, contextKey }: MailOutDialogProps & { sessionKey: string; contextKey: string }) {
  const queryClient = useQueryClient();
  const [restored] = useState(() => readMailSession(sessionKey, queryClient));
  const [recipients, setRecipients] = useState<EmailRecipient[]>(restored?.state.recipients || []);
  const [subject, setSubject] = useState(restored?.state.subject || "");
  const [htmlContent, setHtmlContent] = useState(restored?.state.htmlContent || "");
  const [fromName, setFromName] = useState(restored?.state.fromName ?? "Acolori • Team Update");
  const [fromNameConfirmationOpen, setFromNameConfirmationOpen] = useState(false);
  const [pendingSendHtml, setPendingSendHtml] = useState<string | null>(null);
  const [pendingSendMode, setPendingSendMode] = useState<ShareEmailSendMode>("check");
  const [shareReadinessOpen, setShareReadinessOpen] = useState(false);
  const [shareEmailExcludedIds, setShareEmailExcludedIds] = useState<number[]>(restored?.state.shareEmailExcludedIds || []);
  const [shareEmailThumbnailMode, setShareEmailThumbnailMode] = useState<EmailThumbnailMode>(restored?.state.shareEmailThumbnailMode || 'wait');
  const [thumbnailChoiceOpen, setThumbnailChoiceOpen] = useState(false);
  const [shareReadiness, setShareReadiness] = useState<ShareEmailReadiness | null>(null);
  const [shareReadinessPollExpired, setShareReadinessPollExpired] = useState(false);
  const [deliveryRequestId, setDeliveryRequestId] = useState<string>(() => restored?.deliveryRequestId || newDeliveryRequestId());
  const [deliveryRequestConflict, setDeliveryRequestConflict] = useState<string | null>(restored?.deliveryRequestConflict || null);
  const [attachments, setAttachments] = useState<AttachmentFile[]>(restored?.attachments || []);
  const [selectedEmailAccount, setSelectedEmailAccount] = useState<string>(restored?.state.selectedEmailAccount || "");
  const [selectedSequences, setSelectedSequences] = useState<string[]>(restored?.state.selectedSequences || []);
  const [selectedForwardAttachmentIds, setSelectedForwardAttachmentIds] = useState<string[]>(restored?.state.selectedForwardAttachmentIds || []);
  const [draftId, setDraftId] = useState<string | null>(restored?.draftId || null);
  const [missingAttachmentNames, setMissingAttachmentNames] = useState<string[]>(restored?.missingAttachmentNames || []);
  const [previousComposition, setPreviousComposition] = useState<MailComposeSession | undefined>(restored?.previous);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<{ id: number; subject: string | null } | null>(restored?.state.replyTo || null);
  const [customEmail, setCustomEmail] = useState("");
  const [customType, setCustomType] = useState<'to' | 'cc' | 'bcc'>('to');
  const [showContactForm, setShowContactForm] = useState(false);
  const [newContact, setNewContact] = useState({ firstName: '', lastName: '', clientId: '', category: '', role: 'other' });
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const prefilledRef = useRef(restored?.initialized.prefilled || false);
  const initializedDraftKeyRef = useRef<string | null>(restored?.initialized.draft || null);
  const initializedReplyPulseRef = useRef<number | null>(restored?.initialized.reply || null);
  const initializedForwardPulseRef = useRef<number | null>(restored?.initialized.forward || null);
  const activeForwardPulseRef = useRef<number | null>(restored?.initialized.activeForward || null);
  const fromNameInitializedRef = useRef(restored?.initialized.fromName || false);
  const shareGreetingRef = useRef(restored?.initialized.greeting || '<p>Hallo zusammen,</p>');
  const restoredGreetingRecipientsRef = useRef<string | null>(restored ? JSON.stringify(restored.state.recipients) : null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const quillRef = useRef<ReactQuill>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const canSaveContacts = user?.role === 'admin' || user?.role === 'team';
  // TypeScript prevents this for normal callers; retain an explicit runtime
  // guard for JavaScript/legacy callers rather than silently choosing a mode.
  const hasSourceConflict = !!replySource && !!forwardSource;
  const isReplyMode = !hasSourceConflict && !!replySource;
  const isForwardMode = !hasSourceConflict && !!forwardSource;
  const activeInitialDraft = isForwardMode || hasSourceConflict ? undefined : initialDraft;
  const isShareDraft = !!getShareEmailSendFields(activeInitialDraft, htmlContent, deliveryRequestId, "check").previewShareId;
  const captureSession = (): MailComposeSession => ({
    state: {
      subject, htmlContent, recipients, selectedEmailAccount, selectedSequences, fromName, replyTo,
      selectedForwardAttachmentIds, contextKey,
      shareEmailExcludedIds, shareEmailThumbnailMode,
      attachmentNames: Array.from(new Set([
        ...missingAttachmentNames, ...attachments.map(attachment => attachment.name),
        ...(isForwardMode ? (resolvedForwardSource?.attachments || [])
          .filter(attachment => selectedForwardAttachmentIds.includes(attachment.id)).map(attachment => attachment.fileName) : []),
      ])),
    },
    attachments, deliveryRequestId, deliveryRequestConflict, draftId, missingAttachmentNames,
    initialized: {
      prefilled: prefilledRef.current, draft: initializedDraftKeyRef.current,
      reply: initializedReplyPulseRef.current, forward: initializedForwardPulseRef.current,
      activeForward: activeForwardPulseRef.current, fromName: fromNameInitializedRef.current,
      greeting: shareGreetingRef.current,
    },
    previous: previousComposition,
  });
  useLayoutEffect(() => {
    if (open) writeMailSession(sessionKey, captureSession(), queryClient);
  });

  const historyKey = ['/api/projects', projectId, 'mail-history', user?.id];
  const history = useInfiniteQuery<MailHistoryPage>({
    queryKey: historyKey,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const response = await apiRequest('GET', `/api/projects/${encodeURIComponent(projectId)}/mail-history?offset=${pageParam}`);
      return response.json();
    },
    getNextPageParam: (lastPage, pages) => lastPage.hasMore ? pages.length * 50 : undefined,
    enabled: open && historyOpen && !!user,
    retry: false,
    staleTime: 0,
  });
  const historyItems = sortedMailHistory(history.data?.pages || [], contextKey);
  const saveDraft = useMutation({
    mutationFn: async ({ id, state }: { id: string; state: MailDraftState }) => {
      const response = await apiRequest('POST', `/api/projects/${encodeURIComponent(projectId)}/mail-drafts`, { id, state });
      return response.json() as Promise<{ id: string }>;
    },
    onSuccess: ({ id }) => {
      setDraftId(id);
      void queryClient.invalidateQueries({ queryKey: historyKey });
      toast({ title: "Draft saved", description: "Private to you. Attachments must be reattached when loading a saved draft." });
    },
    onError: (error: Error) => {
      toast({ title: "Could not save draft", description: error.message, variant: "destructive" });
    },
  });
  const removeSavedDraft = async (id: string | null = draftId): Promise<boolean> => {
    if (!id) return true;
    try {
      await apiRequest('DELETE', `/api/projects/${encodeURIComponent(projectId)}/mail-drafts/${encodeURIComponent(id)}`);
      void queryClient.invalidateQueries({ queryKey: historyKey });
      return true;
    } catch {
      toast({ title: "Saved draft could not be removed", description: "It may still appear in previous messages. Do not send it again without checking delivery.", variant: "destructive" });
      return false;
    }
  };
  const freezeInitializers = () => {
    prefilledRef.current = true;
    initializedDraftKeyRef.current = activeInitialDraft?.key || null;
    initializedReplyPulseRef.current = replySource?.pulseId || null;
    initializedForwardPulseRef.current = forwardSource?.pulseId || null;
    activeForwardPulseRef.current = forwardSource?.pulseId || null;
    fromNameInitializedRef.current = true;
  };
  const restoreComposition = (session: MailComposeSession) => {
    setRecipients(session.state.recipients);
    setSubject(session.state.subject);
    setHtmlContent(session.state.htmlContent);
    setSelectedEmailAccount(session.state.selectedEmailAccount || '');
    setSelectedSequences(session.state.selectedSequences || []);
    setFromName(session.state.fromName ?? emailStandards?.fromName ?? 'Acolori • Team Update');
    // Never let a durable draft choose a different authoritative source.
    setReplyTo(isReplyMode && resolvedReplySource
      ? { id: resolvedReplySource.emailId, subject: resolvedReplySource.subject }
      : isForwardMode || activeInitialDraft ? null : session.state.replyTo || null);
    setSelectedForwardAttachmentIds(session.state.selectedForwardAttachmentIds || []);
    setShareEmailExcludedIds(session.state.shareEmailExcludedIds || []);
    setShareEmailThumbnailMode(session.state.shareEmailThumbnailMode || 'wait');
    setAttachments(session.attachments);
    setDeliveryRequestId(session.deliveryRequestId);
    setDeliveryRequestConflict(session.deliveryRequestConflict);
    setDraftId(session.draftId);
    setMissingAttachmentNames(session.missingAttachmentNames);
    shareGreetingRef.current = session.initialized.greeting;
    restoredGreetingRecipientsRef.current = JSON.stringify(session.state.recipients);
    freezeInitializers();
    setHistoryOpen(false);
    setShareReadinessOpen(false);
    setShareReadiness(null);
    setPendingSendHtml(null);
    setFromNameConfirmationOpen(false);
  };
  const loadHistoryState = (state: MailDraftState, id: string | null, sentHasAttachments = false, isSent = false) => {
    // Keep the live session (including File objects and delivery ownership)
    // separately; browsing reusable history must never destroy that session.
    if (!previousComposition) setPreviousComposition(captureSession());
    restoreComposition({
      state: { ...state, htmlContent: sanitizeHtml(neutralizeCidImagesHtml(state.htmlContent)) },
      attachments: [], deliveryRequestId: newDeliveryRequestId(), deliveryRequestConflict: null,
      draftId: id, missingAttachmentNames: state.attachmentNames || [],
      initialized: captureSession().initialized,
    });
    if (sentHasAttachments) {
      toast({ title: "Message loaded without attachments", description: "Reattach any files you want to send. Sent history contains To/Cc only; Bcc is unavailable." });
    } else if (isSent) {
      toast({ title: "Message loaded", description: "To/Cc restored. Bcc is unavailable; no reply thread or attachments were reused." });
    }
  };

  const {
    data: resolvedReplySource,
    isLoading: replySourceLoading,
    isError: replySourceError,
    refetch: refetchReplySource,
  } = useQuery<PulseReplySource>({
    queryKey: ['/api/pulses', replySource?.pulseId, 'reply-source'],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/pulses/${replySource!.pulseId}/reply-source`);
      return response.json();
    },
    enabled: open && isReplyMode && !!replySource?.pulseId,
    staleTime: 30_000,
  });

  const {
    data: resolvedForwardSource,
    isLoading: forwardSourceLoading,
    isError: forwardSourceError,
    refetch: refetchForwardSource,
  } = useQuery<PulseForwardSource>({
    queryKey: ['/api/pulses', forwardSource?.pulseId, 'forward-source'],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/pulses/${forwardSource!.pulseId}/forward-source`);
      return response.json();
    },
    enabled: open && isForwardMode && !!forwardSource?.pulseId,
    staleTime: 30_000,
  });
  const resolvedSource = isForwardMode ? resolvedForwardSource : resolvedReplySource;
  const sourceLoading = isForwardMode ? forwardSourceLoading : replySourceLoading;
  const sourceError = isForwardMode ? forwardSourceError : replySourceError;

  // Optional callers can open the exact Mail Out composer with a purpose-built
  // starting message. Apply it once per opening; never overwrite user edits
  // while recipient queries or parent components re-render.
  useEffect(() => {
    if (!open) return;
    if (!activeInitialDraft || initializedDraftKeyRef.current === activeInitialDraft.key) return;
    initializedDraftKeyRef.current = activeInitialDraft.key;
    setDeliveryRequestId(newDeliveryRequestId());
    setDeliveryRequestConflict(null);
    setShareReadiness(null);
    setShareReadinessOpen(false);
    shareGreetingRef.current = '<p>Hallo zusammen,</p>';
    setSubject(activeInitialDraft.subject);
    setHtmlContent(activeInitialDraft.htmlContent);
  }, [open, activeInitialDraft]);

  // Reply source is resolved from the pulse link, not recreated from the
  // incoming pulse text. Apply its recipients and thread id only once per
  // opening so the user remains in control after editing.
  useEffect(() => {
    if (!open) return;
    if (!isReplyMode || !replySource || !resolvedReplySource) return;
    // Even a saved composition gets its thread identity only from the active
    // authoritative pulse, including when that source resolves after restore.
    if (replyTo?.id !== resolvedReplySource.emailId) {
      setReplyTo({ id: resolvedReplySource.emailId, subject: resolvedReplySource.subject });
    }
    if (initializedReplyPulseRef.current === replySource.pulseId) return;
    initializedReplyPulseRef.current = replySource.pulseId;
    setReplyTo({ id: resolvedReplySource.emailId, subject: resolvedReplySource.subject });
    if (!restored?.state.recipients.length) {
      setRecipients([{ email: resolvedReplySource.fromAddress, name: resolvedReplySource.fromName || undefined, type: 'to' }]);
    }
    if (!restored?.state.subject) setSubject(buildReplySubject(resolvedReplySource.subject, projectId));
    if (resolvedReplySource.emailAccountId) setSelectedEmailAccount(resolvedReplySource.emailAccountId);
  }, [open, replySource, resolvedReplySource, projectId]);

  // Forwarding never preselects original recipients or creates reply headers.
  // This is intentionally one ordered effect: it clears a newly requested
  // source and then immediately initializes it when React Query already has a
  // cached snapshot. Splitting these steps leaves cached reopen/switch cases
  // dependent on a ref mutation, which does not schedule another effect.
  useEffect(() => {
    if (!open) return;
    if (!isForwardMode || !forwardSource) return;
    if (activeForwardPulseRef.current !== forwardSource.pulseId) {
      activeForwardPulseRef.current = forwardSource.pulseId;
      initializedForwardPulseRef.current = null;
      // Clear every source-derived value while a fresh snapshot is loading.
      // If the snapshot is cached, the initialization below wins in this same
      // effect pass; if it is late, the query-data dependency reruns us.
      setRecipients([]);
      setReplyTo(null);
      setSubject("");
      setHtmlContent("");
      setSelectedForwardAttachmentIds([]);
      setSelectedEmailAccount("");
    }
    if (!resolvedForwardSource || initializedForwardPulseRef.current === forwardSource.pulseId) return;
    initializedForwardPulseRef.current = forwardSource.pulseId;
    setReplyTo(null);
    if (!restored) {
      setRecipients([]);
      setHtmlContent("");
    }
    if (!restored?.state.subject) setSubject(ensureSingleProjectId(buildForwardSubject(resolvedForwardSource.subject), projectId));
    setSelectedForwardAttachmentIds(
      (resolvedForwardSource.attachments || []).filter(attachment => attachment.available).map(attachment => attachment.id),
    );
    if (resolvedForwardSource.emailAccountId) setSelectedEmailAccount(resolvedForwardSource.emailAccountId);
  }, [open, isForwardMode, forwardSource?.pulseId, resolvedForwardSource, projectId]);

  // Get available email accounts
  const { data: emailAccounts } = useQuery<EmailSendOption[]>({
    queryKey: ['/api/email-accounts/send-options'],
    enabled: open
  });

  // Sticky From Name (admin-controlled system setting) + templates
  const { data: emailStandards } = useQuery<{ preTemplate: string; postTemplate: string; fromName: string }>({
    queryKey: ['/api/settings/email-standard-templates'],
    enabled: open,
  });

  // Prepare small derivatives while the sender writes; no editor rewrites and
  // no base64 image data in reusable drafts or unfinished sessions.
  const overviewQuery = useQuery<ShareEmailOverview>({
    queryKey: ['/api/preview-shares', activeInitialDraft?.previewShareId, 'email-overview'],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/preview-shares/${activeInitialDraft!.previewShareId}/email-overview`);
      const data = await response.json();
      if (!data || !Array.isArray(data.items) || typeof data.version !== 'string') throw new Error('Unable to load email file overview');
      return data;
    },
    enabled: open && isShareDraft,
    refetchInterval: query => open && isShareDraft
      ? query.state.data?.items.some(item => item.thumbnailStatus === 'pending') ? 3000 : 15000
      : false,
    staleTime: 0,
    retry: false,
    gcTime: 60_000,
  });
  const pendingEmailThumbnails = !!overviewQuery.data?.items.some(item =>
    !shareEmailExcludedIds.includes(item.id) && item.thumbnailStatus === 'pending');

  // Readiness is a short-lived foreground query. Once the server accepts a
  // when-ready/force request, the global durable monitor takes over.
  const { data: polledShareReadiness } = useQuery<ShareEmailReadiness>({
    queryKey: ['/api/preview-shares', activeInitialDraft?.previewShareId, 'email-readiness'],
    queryFn: async () => {
      const response = await apiRequest(
        'GET',
        `/api/preview-shares/${activeInitialDraft!.previewShareId}/email-readiness`,
      );
      return response.json();
    },
    enabled: open && shareReadinessOpen && isShareDraft && !shareReadinessPollExpired,
    refetchInterval: open && shareReadinessOpen && !shareReadinessPollExpired ? 1000 : false,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (polledShareReadiness) setShareReadiness(polledShareReadiness);
  }, [polledShareReadiness]);

  useEffect(() => {
    if (!shareReadinessOpen) {
      setShareReadinessPollExpired(false);
      return;
    }
    const timeout = window.setTimeout(() => setShareReadinessPollExpired(true), 5 * 60 * 1000);
    return () => window.clearTimeout(timeout);
  }, [shareReadinessOpen]);

  // Pre-select a sending identity so the user never has to pick it: their own
  // personal address first, then the shared primary account. The server only
  // returns accounts this user may send from and re-checks on submit.
  useEffect(() => {
    if (!open || !emailAccounts?.length) return;
    // The immutable forward snapshot owns the initial sending identity. This
    // guard also matters when both source and account queries are cache-hot:
    // effects run against the same pre-update render, so a later primary
    // effect would otherwise overwrite the source account.
    if (isForwardMode && resolvedForwardSource?.emailAccountId) return;
    // A reply source may have arrived in a mailbox this user cannot send from
    // (a colleague's personal address). Fall back to the user's own identity
    // instead of leaving an unusable, invisible selection.
    if (selectedEmailAccount && emailAccounts.some(account => account.id === selectedEmailAccount)) return;
    const preferred = emailAccounts.find(account => account.ownership === 'own')
      || emailAccounts.find(account => account.isPrimary)
      || emailAccounts.find(account => /primary/i.test(account.name || ''))
      || emailAccounts[0];
    setSelectedEmailAccount(preferred.id);
  }, [open, emailAccounts, selectedEmailAccount, isForwardMode, resolvedForwardSource?.emailAccountId]);

  // Initialize the From Name from the persisted setting whenever the dialog opens.
  useEffect(() => {
    if (open && emailStandards?.fromName && !fromNameInitializedRef.current) {
      fromNameInitializedRef.current = true;
      setFromName(emailStandards.fromName);
    }
  }, [open, emailStandards?.fromName]);

  // Ranked recipient suggestions + recent groups (task #66)
  const { data: suggestionsData, isLoading: suggestionsLoading } = useQuery<SuggestionsResponse>({
    queryKey: ['/api/projects', projectId, 'recipient-suggestions'],
    enabled: open && !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: true,
    // Other users, inbound mail and client/contact edits can change rankings
    // while this composer stays open. Refresh without altering chosen recipients.
    refetchInterval: open ? 30_000 : false,
  });

  // Preview/Final share drafts alone personalize their leading greeting from
  // the selected external recipients. Only replace the greeting we generated
  // previously so a manually edited salutation always remains untouched.
  useEffect(() => {
    if (!open || !activeInitialDraft?.shareGreeting || !suggestionsData) return;
    if (restoredGreetingRecipientsRef.current !== null) {
      if (restoredGreetingRecipientsRef.current === JSON.stringify(recipients)) return;
      restoredGreetingRecipientsRef.current = null;
    }
    const nextGreeting = getShareEmailGreeting(recipients, suggestionsData);
    const previousGreeting = shareGreetingRef.current;
    setHtmlContent(current =>
      updateShareEmailGreeting(current, previousGreeting, nextGreeting),
    );
    shareGreetingRef.current = nextGreeting;
  }, [open, activeInitialDraft?.shareGreeting, activeInitialDraft?.key, recipients, suggestionsData]);

  // Clients for the inline "save as contact" form
  const { data: clientsList } = useQuery<any[]>({
    queryKey: ['/api/clients'],
    enabled: open && showContactForm,
  });

  // Prefill "Send to" once per dialog open: default to the most recent
  // contact group (handled as a reply), falling back to top-ranked contacts.
  useEffect(() => {
    if (!open) return;
    if (prefilledRef.current || !suggestionsData || isReplyMode || isForwardMode) return;
    prefilledRef.current = true;
    if (recipients.length > 0) return;

    const groups = suggestionsData.groups || [];
    const newest = groups
      .slice()
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0];

    if (newest && newest.members.length > 0) {
      if (restored) {
        // A session may have closed before suggestions resolved. Complete the
        // missing recipient prefill without replacing its authored subject or
        // silently turning the restored composition into a reply.
        setRecipients(newest.members.map(member => ({ email: member.email, name: member.name || undefined, type: member.type })));
        return;
      }
      applyGroup(newest, { silent: true });
      return;
    }

    const top = (suggestionsData.suggestions || [])
      // Broader client contacts are offered, never automatically selected.
      .filter(s => !s.isInternal && s.score >= 1 &&
        (!s.source || s.source === 'project_history' || s.source === 'project_contact'))
      .slice(0, 3);
    if (top.length > 0) {
      setRecipients(top.map(s => ({ email: s.email, name: s.name || undefined, type: 'to' as const })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, suggestionsData]);

  // Run after initialization effects as well as layout commits so the
  // initialization guards survive unmount even when an initializer set no state.
  useEffect(() => {
    if (open) writeMailSession(sessionKey, captureSession(), queryClient);
  });

  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      // projectId also goes in the query string: the server's project access
      // check runs BEFORE the multipart body is parsed, so it can't read the
      // form field — without this the server answers "Project ID required".
      const response = await apiRequest('POST', `/api/emails/send?projectId=${encodeURIComponent(projectId)}`, formData);
      return await response.json();
    },
    onSuccess: async (data) => {
      await removeSavedDraft();
      // Share deliveries are accepted into a durable queue. A 202 response
      // is not SMTP confirmation and must never be presented as "sent".
      if (isShareDraft && data?.queued && data?.delivery) {
        toast({
          title: "Email Queued",
          description: "Automatic sending is queued. You can continue working while the files finish preparing.",
        });
        setShareReadinessOpen(false);
        setShareReadiness(null);
        await queryClient.invalidateQueries({ queryKey: ['/api/emails/share-deliveries'] });
        resetForm();
        onOpenChange(false);
        return;
      }

      if (selectedSequences.length > 0 && data.pulseId) {
        for (const sequenceNumber of selectedSequences) {
          try {
            await apiRequest('POST', `/api/pulses/${data.pulseId}/sequences`, { sequenceNumber });
          } catch (error) {
            console.error(`Failed to assign sequence ${sequenceNumber} to email pulse:`, error);
          }
        }
      }

      if (Array.isArray(data?.warnings) && data.warnings.length > 0) {
        toast({
          title: "Email Sent — please check the pulse",
          description: data.warnings.join(' '),
        });
      } else {
        toast({
          title: "Email Sent",
          description: "Your email has been sent successfully and a pulse has been created.",
        });
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/years'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'pulses', 'paginated'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['/api/pulses/batch'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'recipient-suggestions'] }),
      ]);

      resetForm();
      onOpenChange(false);
    },
    onError: (error: any) => {
      const body = error?.body;
      if (body?.code === 'SHARE_THUMBNAILS_PENDING') {
        setThumbnailChoiceOpen(true);
        void overviewQuery.refetch();
        return;
      }
      if (body?.code === 'SHARE_OVERVIEW_CHANGED') {
        void overviewQuery.refetch();
        toast({ title: 'Shared files changed', description: body.error, variant: 'destructive' });
        return;
      }
      if (body?.code === 'DELIVERY_REQUEST_CONTENT_MISMATCH' || body?.code === 'EMAIL_REQUEST_PATH_CONFLICT') {
        // A durable queued/sent attempt may own this UUID. Never turn a
        // conflict into a fresh send, including when the share link was edited
        // away and the submission now takes the ordinary email path.
        const state = body.delivery?.state ?? body.state;
        const knownState = ['waiting', 'needs_confirmation', 'sending', 'sent', 'failed', 'uncertain', 'canceled', 'expired'].includes(state);
        const message = `This request already belongs to an existing delivery.${knownState ? ` Existing delivery state: ${state}.` : ' It may already be queued or sent.'} Nothing was sent again. Check or cancel that delivery before starting a different email.`;
        // A close/unmount can happen while the request is in flight. React's
        // setter then has no mounted owner, but the retained session must
        // still remember this block before the next reopen.
        const retained = readMailSession(sessionKey, queryClient);
        if (retained?.deliveryRequestId === deliveryRequestId) {
          writeMailSession(sessionKey, { ...retained, deliveryRequestConflict: message }, queryClient);
        }
        setDeliveryRequestConflict(message);
        setShareReadinessOpen(false);
        toast({ title: "Sending blocked — existing delivery conflict", description: message, variant: "destructive", duration: 15000 });
        return;
      }
      if (isShareDraft && body?.code === "SHARE_FILES_NOT_READY" && body.readiness) {
        setShareReadiness(body.readiness as ShareEmailReadiness);
        // This request was explicitly not queued. A subsequent action is a
        // new frozen attempt; network failures, by contrast, preserve this
        // id because useMutation has no automatic replay.
        setDeliveryRequestId(newDeliveryRequestId());
        setShareReadinessOpen(true);
        return;
      }
      // Ordinary sends: the server already holds an outcome for this
      // composition. Nothing was sent again; close the composer so the user
      // cannot keep resubmitting the same mail.
      if (!isShareDraft && body?.code === 'EMAIL_SEND_ALREADY_SUBMITTED') {
        void removeSavedDraft();
        toast({
          title: body.state === 'sent' ? "Email already sent" : body.state === 'uncertain' ? "Delivery uncertain" : "Email is being sent",
          description: body.error,
          variant: body.state === 'sent' ? undefined : "destructive",
        });
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'pulses', 'paginated'] }),
          queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId] }),
        ]);
        resetForm();
        onOpenChange(false);
        return;
      }
      // The mail MAY have been delivered. Keep the composer closed and the
      // pulse visible; the user decides explicitly whether a new email is
      // needed — never a plain "try again".
      if (!isShareDraft && body?.code === 'EMAIL_SEND_UNCERTAIN') {
        void removeSavedDraft();
        toast({
          title: "Delivery uncertain — do not resend blindly",
          description: body.error,
          variant: "destructive",
          duration: 15000,
        });
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'pulses', 'paginated'] }),
          queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId] }),
        ]);
        resetForm();
        onOpenChange(false);
        return;
      }
      toast({
        title: "Send Failed",
        description: error.message || "Failed to send email. Please try again.",
        variant: "destructive",
      });
    }
  });

  // Save custom address as a real contact linked to the project
  const saveContactMutation = useMutation({
    mutationFn: async () => {
      const body: any = {
        firstName: newContact.firstName.trim() || customEmail.split('@')[0],
        lastName: newContact.lastName.trim() || '-',
        email: customEmail.trim(),
        clientId: newContact.clientId || null,
      };
      if (newContact.category) body.category = newContact.category;
      const created = await (await apiRequest('POST', '/api/contact-persons', body)).json();
      const contactId = created?.id || created?.contactPerson?.id;
      if (contactId) {
        await apiRequest('POST', `/api/projects/${projectId}/contacts/${contactId}/link`, { role: newContact.role });
      }
      return created;
    },
    onSuccess: () => {
      toast({ title: "Contact saved", description: `${customEmail} was saved and linked to this project.` });
      addRecipientDirect(customEmail, newContact.firstName ? `${newContact.firstName} ${newContact.lastName}`.trim() : undefined, customType);
      setCustomEmail("");
      setShowContactForm(false);
      setNewContact({ firstName: '', lastName: '', clientId: '', category: '', role: 'other' });
      queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'recipient-suggestions'] });
    },
    onError: (error: any) => {
      toast({ title: "Could not save contact", description: error.message, variant: "destructive" });
    }
  });

  const resetForm = () => {
    // Clear synchronously: the parent may unmount instead of committing our
    // state setters when the terminal send callback closes this dialog.
    if (readMailSession(sessionKey, queryClient)?.deliveryRequestId === deliveryRequestId) {
      deleteMailSession(sessionKey, queryClient);
    }
    setDraftId(null);
    setMissingAttachmentNames([]);
    setPreviousComposition(undefined);
    setHistoryOpen(false);
    fromNameInitializedRef.current = false;
    setRecipients([]);
    setSubject("");
    setHtmlContent("");
    setFromName("Acolori • Team Update");
    setFromNameConfirmationOpen(false);
    setPendingSendHtml(null);
    setPendingSendMode("check");
    setShareReadinessOpen(false);
    setShareReadiness(null);
    setShareReadinessPollExpired(false);
    setShareEmailExcludedIds([]);
    setShareEmailThumbnailMode('wait');
    setThumbnailChoiceOpen(false);
    setDeliveryRequestId(newDeliveryRequestId());
    setDeliveryRequestConflict(null);
    setAttachments([]);
    setSelectedEmailAccount("");
    setSelectedSequences([]);
    setReplyTo(null);
    setCustomEmail("");
    setShowContactForm(false);
    setPickerOpen(false);
    shareGreetingRef.current = '<p>Hallo zusammen,</p>';
    restoredGreetingRecipientsRef.current = null;
    // Only explicit discard or terminal delivery starts a new send attempt.
    initializedDraftKeyRef.current = null;
    prefilledRef.current = false;
    initializedReplyPulseRef.current = null;
    initializedForwardPulseRef.current = null;
    activeForwardPulseRef.current = null;
    setSelectedForwardAttachmentIds([]);
  };

  const addRecipientDirect = (email: string, name: string | undefined, type: 'to' | 'cc' | 'bcc') => {
    const normalized = email.trim().toLowerCase();
    setRecipients(prev => {
      if (prev.some(r => r.email.toLowerCase() === normalized)) return prev;
      return [...prev, { email: email.trim(), name, type }];
    });
  };

  const removeRecipient = (index: number) => {
    setRecipients(prev => prev.filter((_, i) => i !== index));
  };

  const applyGroup = (group: RecipientGroup, opts?: { silent?: boolean }) => {
    setRecipients(group.members.map(m => ({ email: m.email, name: m.name || undefined, type: m.type })));
    // Recipient shortcuts must not change an explicit reply's source/thread.
    // Share delivery likewise preserves its announcement subject and body.
    if (activeInitialDraft || isReplyMode) {
      if (!opts?.silent) setPickerOpen(false);
      return;
    }
    if (isForwardMode) {
      // Picking a group remains an explicit user action, but never turns a
      // forward into a reply or imports source BCC recipients.
      setReplyTo(null);
    } else if (group.sourceEmailId) {
      setReplyTo({ id: group.sourceEmailId, subject: group.subject });
      if (group.subject) {
        const base = group.subject.trim();
        setSubject(/^re\s*:/i.test(base) ? base : `Re: ${base}`);
      }
    } else {
      setReplyTo(null);
    }
    if (!opts?.silent) setPickerOpen(false);
  };

  const handleCustomAdd = () => {
    const email = customEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({ title: "Invalid Email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    const known = suggestionsData?.suggestions?.find(s => s.email === email.toLowerCase() && s.contactId);
    if (known || !canSaveContacts) {
      addRecipientDirect(email, known?.name || undefined, customType);
      setCustomEmail("");
      return;
    }
    // Unknown address: open the inline save-as-contact form (no extra clicks —
    // the same popup assigns client + priority type and saves).
    setShowContactForm(true);
  };

  // Drag & drop: reorder recipient chips / drop suggestions into the list
  const onChipDragStart = (index: number) => setDragIndex(index);
  const onChipDrop = (index: number) => {
    if (dragIndex === null || dragIndex === index) return;
    setRecipients(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(null);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    files.forEach(file => {
      if (file.size > 25 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: `${file.name} is too large. Maximum size is 25MB.`,
          variant: "destructive",
        });
        return;
      }
      const attachment: AttachmentFile = { file, name: file.name, size: file.size };
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          attachment.preview = e.target?.result as string;
          setAttachments(prev => [...prev, attachment]);
        };
        reader.readAsDataURL(file);
      } else {
        setAttachments(prev => [...prev, attachment]);
      }
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleImageDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        if (quillRef.current) {
          const quill = quillRef.current.getEditor();
          const range = quill.getSelection();
          quill.insertEmbed(range?.index || 0, 'image', imageUrl);
        }
      };
      reader.readAsDataURL(file);
    });
  }, []);

  /**
   * Content copied from the upper source pane is placed on the clipboard as
   * a real HTML quote. Pasting it into Quill therefore keeps a clear German
   * source label and blockquote styling instead of blending into the reply.
   */
  const handleOriginalEmailCopy = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
    const range = selection.getRangeAt(0);
    if (!event.currentTarget.contains(range.commonAncestorContainer)) return;

    const selected = document.createElement('div');
    selected.appendChild(range.cloneContents());
    const quotedHtml =
      '<div data-original-email-excerpt="true">' +
      '<p><strong>Aus der Original-E-Mail:</strong></p>' +
      `<blockquote>${selected.innerHTML}</blockquote></div><p><br></p>`;
    event.clipboardData.setData('text/html', quotedHtml);
    event.clipboardData.setData(
      'text/plain',
      `Aus der Original-E-Mail:\n${selection.toString()}`,
    );
    event.preventDefault();
  }, []);

  const validateCompose = (): boolean => {
    if (deliveryRequestConflict) return false;
    if (hasSourceConflict) {
      toast({
        title: "Invalid composer mode",
        description: "Choose either a reply source or a forward source, not both.",
        variant: "destructive",
      });
      return false;
    }
    if ((isReplyMode && (!resolvedReplySource || !replyTo)) || (isForwardMode && (!resolvedForwardSource || forwardSourceError))) {
      toast({
        title: isForwardMode ? "Forward source unavailable" : "Reply source unavailable",
        description: isForwardMode
          ? "The original email must load before this forward can be sent."
          : "The incoming email must load before this reply can be sent.",
        variant: "destructive",
      });
      return false;
    }
    if (isForwardMode && forwardAttachmentLimitError) {
      toast({
        title: "Forward attachment limit exceeded",
        description: forwardAttachmentLimitError,
        variant: "destructive",
      });
      return false;
    }
    if (!selectedEmailAccount) {
      toast({ title: "No Email Account", description: "Please select an email account to send from.", variant: "destructive" });
      return false;
    }
    if (recipients.length === 0) {
      toast({ title: "No Recipients", description: "Please add at least one recipient.", variant: "destructive" });
      return false;
    }
    if (!subject.trim()) {
      toast({ title: "No Subject", description: "Please enter an email subject.", variant: "destructive" });
      return false;
    }
    if (!isForwardMode && !htmlContent.replace(/<[^>]*>/g, ' ').trim()) {
      toast({ title: "No Content", description: "Please enter email content.", variant: "destructive" });
      return false;
    }
    return true;
  };

  const submitEmail = (
    finalHtml: string,
    confirmFromNameChange: boolean,
    shareSendMode: ShareEmailSendMode = "check",
  ) => {
    if (deliveryRequestConflict) return;
    const formData = new FormData();
    const canonicalSubject = ensureSingleProjectId(subject, projectId);
    if (canonicalSubject !== subject) setSubject(canonicalSubject);
    formData.append('projectId', projectId);
    formData.append('emailAccountId', selectedEmailAccount);
    formData.append('subject', canonicalSubject);
    formData.append('htmlContent', finalHtml);
    // Preserve the artist-authored editor body for the pulse's main-table title.
    formData.append('pulseTitleSourceHtml', htmlContent);
    if (user?.role === 'admin') {
      formData.append('fromName', fromName.trim());
      if (confirmFromNameChange) {
        formData.append('confirmFromNameChange', 'true');
      }
    }
    formData.append('recipients', JSON.stringify(recipients));
    // One composition = one deliveryRequestId. The server guarantees at most
    // one SMTP submission per id, so a double click, a client timeout, or a
    // retry after a network error can never send the same email twice. The
    // id is only renewed after a definite outcome (see mutation callbacks).
    const shareFields = getShareEmailSendFields(activeInitialDraft, finalHtml, deliveryRequestId, shareSendMode);
    for (const [key, value] of Object.entries(shareFields)) formData.append(key, value);
    if (shareFields.previewShareId) {
      if (!overviewQuery.data || overviewQuery.isError) {
        toast({ title: 'File overview unavailable', description: 'Reload the file overview before sending.', variant: 'destructive' });
        return;
      }
      formData.append('shareEmailOverview', JSON.stringify({
        selectedIds: overviewQuery.data.items.filter(item => !shareEmailExcludedIds.includes(item.id)).map(item => item.id),
        mode: shareEmailThumbnailMode,
        version: overviewQuery.data.version,
      }));
      // Durable deliveries cannot receive the pulse sequence assignment from
      // the normal post-send callback (there is no pulse in the 202 response).
      // Preserve the first numeric sequence selected in the composer so the
      // delivery worker can assign it after SMTP confirmation. Normal sends
      // still attach every selected sequence in onSuccess below.
      appendShareSequenceFields(formData, selectedSequences);
    }
    if (replyTo && !isForwardMode) {
      formData.append('replyToEmailId', String(replyTo.id));
    }
    if (isForwardMode && forwardSource) {
      formData.append('forwardSourcePulseId', String(forwardSource.pulseId));
      formData.append('forwardAttachmentIds', JSON.stringify(selectedForwardAttachmentIds));
    }
    attachments.forEach((attachment) => {
      formData.append('attachments', attachment.file);
    });
    sendEmailMutation.mutate(formData);
  };

  const doSend = (finalHtml: string, shareSendMode: ShareEmailSendMode = "check") => {
    const configuredFromName = (emailStandards?.fromName || 'Acolori • Team Update').trim();
    const requestedFromName = fromName.trim();
    if (user?.role === 'admin' && !requestedFromName) {
      toast({
        title: "From Name required",
        description: "Enter the sender name that recipients should see.",
        variant: "destructive",
      });
      return;
    }
    if (user?.role === 'admin' && requestedFromName !== configuredFromName) {
      setPendingSendHtml(finalHtml);
      setPendingSendMode(shareSendMode);
      setFromNameConfirmationOpen(true);
      return;
    }
    submitEmail(finalHtml, false, shareSendMode);
  };

  const confirmFromNameChange = () => {
    if (pendingSendHtml === null) return;
    const finalHtml = pendingSendHtml;
    setPendingSendHtml(null);
    const shareSendMode = pendingSendMode;
    setPendingSendMode("check");
    setFromNameConfirmationOpen(false);
    submitEmail(finalHtml, true, shareSendMode);
  };

  const handleSendEmail = () => {
    if (!validateCompose()) return;
    if (isShareDraft && shareEmailThumbnailMode === 'wait' && pendingEmailThumbnails) {
      setThumbnailChoiceOpen(true);
      return;
    }
    doSend(htmlContent, "check");
  };

  const handleShareReadinessAction = (mode: "when_ready" | "force") => {
    if (!isShareDraft) return;
    doSend(htmlContent, mode);
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const suggestions = suggestionsData?.suggestions || [];
  const suggestionSections = [
    { key: 'project_history', title: 'Used in this project', description: 'Ranked by recent and repeated correspondence' },
    { key: 'project_contact', title: 'Linked project contacts', description: 'Contacts explicitly assigned to this project' },
    { key: 'client_history', title: 'Recent client contacts', description: 'From other permitted correspondence for this client' },
    { key: 'client_contact', title: 'Other client email addresses', description: 'Known contacts and mailboxes for the project’s client' },
    { key: 'internal', title: 'Internal project team', description: '' },
  ].map(section => ({
    ...section,
    contacts: suggestions.filter(s => (s.source || (s.isInternal ? 'internal' : 'project_contact')) === section.key),
  })).filter(section => section.contacts.length > 0);
  const groups = suggestionsData?.groups || [];
  const recipientEmails = new Set(recipients.map(r => r.email.toLowerCase()));
  const sendIdentityIsValid = !!selectedEmailAccount &&
    !!emailAccounts?.some(account => account.id === selectedEmailAccount);
  const selectedForwardAttachments = (resolvedForwardSource?.attachments || [])
    .filter(attachment => selectedForwardAttachmentIds.includes(attachment.id));
  const unreattachedNames = missingAttachmentNames.filter(name =>
    !attachments.some(attachment => attachment.name === name) &&
    !(isForwardMode && selectedForwardAttachments.some(attachment => attachment.available && attachment.fileName === name)));
  const combinedForwardAttachmentCount = attachments.length + selectedForwardAttachments.length;
  // An unavailable source attachment cannot be selected. A null source size is
  // deliberately not guessed: the send route verifies those raw bytes before
  // delivery, while this catches every limit breach the composer can know.
  const combinedKnownForwardAttachmentBytes = attachments.reduce((total, attachment) => total + attachment.size, 0) +
    selectedForwardAttachments.reduce((total, attachment) => total + (attachment.size || 0), 0);
  const forwardAttachmentLimitError = !isForwardMode
    ? null
    : combinedForwardAttachmentCount > FORWARD_COMBINED_ATTACHMENT_MAX_COUNT
      ? `A forward can include at most ${FORWARD_COMBINED_ATTACHMENT_MAX_COUNT} files (original and new files combined).`
      : combinedKnownForwardAttachmentBytes > FORWARD_COMBINED_ATTACHMENT_MAX_BYTES
        ? 'Original and new attachments together cannot exceed 25 MB.'
        : null;
  const selectedForwardAttachmentsAreValid = !isForwardMode ||
    !!resolvedForwardSource &&
    selectedForwardAttachmentIds.every(id => resolvedForwardSource.attachments.some(attachment => attachment.id === id && attachment.available));
  const sendDisabled = sendEmailMutation.isPending ||
    saveDraft.isPending ||
    !!deliveryRequestConflict ||
    hasSourceConflict ||
    !sendIdentityIsValid ||
    recipients.length === 0 ||
    !subject.trim() ||
    (isReplyMode && (!resolvedReplySource || !replyTo || replySourceLoading || replySourceError)) ||
    (isForwardMode && (!resolvedForwardSource || forwardSourceLoading || forwardSourceError || !selectedForwardAttachmentsAreValid || !!forwardAttachmentLimitError));

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // The readiness decision has explicit actions. Escape/the dialog's X
        // must not silently discard the editable draft underneath it.
        if (!o && (shareReadinessOpen || saveDraft.isPending)) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">
        <DialogHeader className="relative">
          <div className="flex justify-between items-start">
            <div>
              <DialogTitle className="text-xl font-bold text-gray-100 flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-400" />
                {composeLabel ? `Send ${composeLabel}` : 'Send Email'} - Project {projectId}
              </DialogTitle>
            </div>
            <div className="absolute top-0 right-0 min-w-0 max-w-[200px]">
              <PulseSequenceTags
                pulseId={null}
                projectId={projectId}
                selectedSequences={selectedSequences}
                onSequenceChange={setSelectedSequences}
                isCreationMode={true}
                className="w-full"
                showInlineAllLink={true}
                labelText="Sequence Tags"
              />
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-700 pb-3">
            <Button type="button" variant="outline" size="sm"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              disabled={sendEmailMutation.isPending || saveDraft.isPending || shareReadinessOpen || !!deliveryRequestConflict || hasSourceConflict}
              onClick={() => {
                const id = draftId || newDeliveryRequestId();
                setDraftId(id);
                saveDraft.mutate({ id, state: captureSession().state });
              }}>
              {saveDraft.isPending ? 'Saving draft...' : 'Save as draft'}
            </Button>
            <Button type="button" variant="outline" size="sm"
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
              disabled={sendEmailMutation.isPending || saveDraft.isPending || shareReadinessOpen || !!deliveryRequestConflict || hasSourceConflict}
              onClick={() => setHistoryOpen(value => !value)}>
              <Clock className="mr-2 h-3.5 w-3.5" />
              {historyOpen ? 'Hide previous messages' : contextKey === 'normal' ? 'Load previous messages' : 'Load saved drafts'}
            </Button>
            {draftId && <span className="text-xs text-gray-400">Private draft</span>}
            {previousComposition && <Button type="button" variant="ghost" size="sm"
              disabled={sendEmailMutation.isPending || saveDraft.isPending || shareReadinessOpen}
              onClick={() => {
                restoreComposition(previousComposition);
                setPreviousComposition(undefined);
              }}>Return to previous composition</Button>}
            <Button type="button" variant="ghost" size="sm" className="ml-auto text-gray-400"
              disabled={sendEmailMutation.isPending || saveDraft.isPending || shareReadinessOpen}
              onClick={() => setDiscardOpen(true)}>Discard / start fresh</Button>
          </div>
          {historyOpen && (
            <section aria-label="Previous messages" className="rounded-md border border-gray-700 bg-gray-800/40 p-3 space-y-2">
              <p className="text-xs text-gray-400">
                Newest first. Drafts are private to you.
                {contextKey !== 'normal' ? ' Only drafts for this source are shown.' : ' Sent messages restore text and To/Cc, not Bcc, attachments or reply threads.'}
              </p>
              {history.isPending && <div aria-label="Loading previous messages" className="space-y-2">
                <div className="h-8 rounded bg-gray-700/60 animate-pulse" /><div className="h-8 rounded bg-gray-700/60 animate-pulse" />
              </div>}
              {history.isError && <div role="alert" className="flex items-center justify-between text-sm text-red-300">
                <span>Could not load previous messages.</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => void history.refetch()}>Retry</Button>
              </div>}
              {!history.isPending && !history.isError && historyItems.length === 0 &&
                <div className="py-4 text-sm text-gray-400">No previous messages yet. Save a private draft to return to it later.</div>}
              <div className="max-h-56 overflow-y-auto divide-y divide-gray-700">
                {historyItems.map(item => (
                  <button type="button" key={`${item.kind}:${item.kind === 'draft' ? item.draft.id : item.message.id}`}
                    className="w-full flex items-center gap-3 px-2 py-2 text-left hover:bg-gray-700/50 rounded text-sm"
                    onClick={() => item.kind === 'draft'
                      ? loadHistoryState(reusableDraftState(item.draft.state, contextKey), item.draft.state.contextKey === contextKey ? item.draft.id : null)
                      : loadHistoryState(reusableSentState(item.message), null, item.message.hasAttachments, true)}>
                    <Badge variant="outline" className={item.kind === 'draft' ? 'border-amber-700 text-amber-300' : 'border-gray-600 text-gray-400'}>
                      {item.kind === 'draft' ? 'Draft' : 'Sent'}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate text-gray-200">{(item.kind === 'draft' ? item.draft.subject : item.message.subject) || '(No subject)'}</span>
                    <time className="shrink-0 text-xs text-gray-400">{item.date ? new Date(item.date).toLocaleString() : 'Date unavailable'}</time>
                  </button>
                ))}
              </div>
              {contextKey === 'normal' && history.hasNextPage && <Button type="button" variant="ghost" size="sm"
                disabled={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>
                {history.isFetchingNextPage ? 'Loading...' : 'Load older messages'}
              </Button>}
              {history.isFetchNextPageError && <p role="alert" className="text-xs text-red-300">Older messages could not be loaded. Try again.</p>}
            </section>
          )}
          {unreattachedNames.length > 0 && <div role="alert" className="rounded-md border border-amber-800 bg-amber-950/50 px-3 py-2 text-sm text-amber-200">
            Saved drafts do not include files. Reattach before sending: {unreattachedNames.join(', ')}.
          </div>}
          {hasSourceConflict && (
            <div className="rounded-md border border-rose-800 bg-rose-950/50 px-3 py-2 text-sm text-rose-200" role="alert">
              This composer cannot use a reply source and a forward source at the same time.
            </div>
          )}
          {isForwardMode && resolvedForwardSource?.emailAccountId && emailAccounts
            && !emailAccounts.some(account => account.id === resolvedForwardSource.emailAccountId) && (
            <div className="rounded-md border border-amber-800 bg-amber-950/50 px-3 py-2 text-sm text-amber-200" role="alert">
              This email arrived in a personal mailbox you cannot send from. Forwards keep the receiving mailbox as sender, so only its owner can forward it.
            </div>
          )}
          {/* Email Account Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-200 font-medium">Send From</Label>
              <Select value={selectedEmailAccount} onValueChange={setSelectedEmailAccount}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-gray-200">
                  <SelectValue placeholder="Select email account" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {emailAccounts?.map((account) => (
                    <SelectItem key={account.id} value={account.id} className="text-gray-200">
                      {accountDisplayName(account.name)} ({account.email})
                      {account.ownership === 'own' ? ' · your address' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* From Name is admin-only: it is a sticky global setting, not a
                per-email field. Non-admins always send with the stored name. */}
            {user?.role === 'admin' && (
              <div>
                <Label className="text-gray-200 font-medium">From Name (global)</Label>
                <Input
                  value={fromName}
                  onChange={(e) => { fromNameInitializedRef.current = true; setFromName(e.target.value); }}
                  placeholder="Your name or company"
                  className="bg-gray-800 border-gray-600 text-gray-200"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Admin only. Changes require confirmation and apply to all future emails.
                </p>
              </div>
            )}
          </div>

          {/* Send To block (task #66) */}
          <div>
            <Label className="text-gray-200 font-medium">Send to</Label>
            <div
              className="mt-1 min-h-[46px] w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 flex flex-wrap items-center gap-2 cursor-pointer hover:border-blue-500 transition-colors"
              onClick={() => setPickerOpen(v => !v)}
              onDragOver={(e) => e.preventDefault()}
              data-testid="send-to-block"
            >
              {recipients.length === 0 ? (
                <span className="text-gray-400 text-sm flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {suggestionsLoading ? 'Loading likely recipients…' : 'Click to pick recipients'}
                </span>
              ) : (
                recipients.map((recipient, index) => (
                  <Badge
                    key={`${recipient.email}-${index}`}
                    variant="secondary"
                    draggable
                    onDragStart={(e) => { e.stopPropagation(); onChipDragStart(index); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onChipDrop(index); }}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-gray-700 text-gray-200 px-2.5 py-1 flex items-center gap-1.5 cursor-grab"
                  >
                    <GripVertical className="w-3 h-3 text-gray-500" />
                    <span className={`text-xs uppercase font-bold ${recipient.type === 'to' ? 'text-blue-400' : recipient.type === 'cc' ? 'text-teal-400' : 'text-purple-400'}`}>
                      {recipient.type}
                    </span>
                    {recipient.name ? `${recipient.name}` : recipient.email}
                    <X
                      className="w-3 h-3 cursor-pointer hover:text-red-400"
                      onClick={(e) => { e.stopPropagation(); removeRecipient(index); }}
                    />
                  </Badge>
                ))
              )}
              <span className="ml-auto text-gray-500 text-xs">{pickerOpen ? '▲' : '▼'}</span>
            </div>

            {/* Reply banner */}
            {replyTo && !isForwardMode && (
              <div className="mt-2 flex items-center gap-2 text-sm bg-blue-950/60 border border-blue-800 rounded-md px-3 py-1.5 text-blue-300">
                <Reply className="w-4 h-4 shrink-0" />
                <span className="truncate">Sent as reply{replyTo.subject ? ` to: ${replyTo.subject}` : ''} — keeps the conversation thread alive</span>
                {!isReplyMode && (
                  <X
                    className="w-4 h-4 ml-auto cursor-pointer hover:text-red-400 shrink-0"
                    onClick={() => setReplyTo(null)}
                    aria-label="Send as new email instead"
                  />
                )}
              </div>
            )}

            {/* Picker */}
            {pickerOpen && (
              <div className="mt-2 rounded-md border border-gray-600 bg-gray-850 bg-gray-800/60 p-3 space-y-4">
                {/* Recent groups */}
                {groups.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" /> Recent contact groups
                    </div>
                    <div className="space-y-1.5">
                      {groups.map((group, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => applyGroup(group)}
                          className="w-full text-left rounded border border-gray-700 hover:border-blue-500 bg-gray-800 px-3 py-2 transition-colors"
                        >
                          <div className="flex items-center gap-2 text-sm text-gray-200">
                            {group.kind === 'last_email_out' ? <Send className="w-3.5 h-3.5 text-blue-400" /> : <Reply className="w-3.5 h-3.5 text-teal-400" />}
                            <span className="font-medium">
                              {group.kind === 'last_email_out' ? 'Last email out' : 'Incoming'}
                            </span>
                            {group.subject && <span className="text-gray-400 truncate">— {group.subject}</span>}
                            {group.date && <span className="text-gray-500 text-xs ml-auto shrink-0">{new Date(group.date).toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}</span>}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5 truncate">
                            {group.members.map(m => m.name || m.email).join(', ')}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Project correspondence first, client-wide suggestions below it. */}
                <div>
                  {suggestions.length === 0 ? (
                    <p className="text-sm text-gray-500">No known project or client contacts yet.</p>
                  ) : (
                    <div className="max-h-72 overflow-y-auto space-y-4">
                      {suggestionSections.map(section => (
                        <section key={section.key} data-testid={`recipient-section-${section.key}`}>
                          <div className="text-xs uppercase tracking-wide text-gray-400 mb-1 flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" /> {section.title}
                          </div>
                          {section.description && <p className="text-xs text-gray-500 mb-2">{section.description}</p>}
                      {section.contacts.map((s) => {
                        const added = recipientEmails.has(s.email);
                        return (
                          <div
                            key={s.email}
                            draggable={!added}
                            onDragEnd={() => { if (!added) addRecipientDirect(s.email, s.name || undefined, 'to'); }}
                            className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm ${added ? 'opacity-50' : 'hover:bg-gray-700 cursor-pointer'}`}
                            onClick={() => { if (!added) addRecipientDirect(s.email, s.name || undefined, 'to'); }}
                          >
                            <GripVertical className="w-3.5 h-3.5 text-gray-600 shrink-0" />
                            <span className="text-gray-200 truncate">{s.name || s.email}</span>
                            {s.name && <span className="text-gray-500 text-xs truncate">{s.email}</span>}
                            <span className="ml-auto flex items-center gap-1.5 shrink-0">
                              {s.isInternal && <Badge variant="outline" className="text-[10px] border-purple-600 text-purple-300 px-1 py-0">internal</Badge>}
                              {s.role && <Badge variant="outline" className="text-[10px] border-blue-700 text-blue-300 px-1 py-0">{roleLabel(s.role)}</Badge>}
                              {s.category && <Badge variant="outline" className="text-[10px] border-gray-600 text-gray-400 px-1 py-0">{s.category}</Badge>}
                              {!added && (
                                <Button
                                  type="button" size="sm" variant="ghost"
                                  className="h-6 px-1.5 text-teal-400 hover:text-teal-300"
                                  onClick={(e) => { e.stopPropagation(); addRecipientDirect(s.email, s.name || undefined, 'cc'); }}
                                >
                                  +CC
                                </Button>
                              )}
                            </span>
                          </div>
                        );
                      })}
                        </section>
                      ))}
                    </div>
                  )}
                </div>

                {/* Custom address */}
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5">
                    <UserPlus className="w-3.5 h-3.5" /> Custom address
                  </div>
                  <div className="flex gap-2">
                    <Select value={customType} onValueChange={(v: 'to' | 'cc' | 'bcc') => setCustomType(v)}>
                      <SelectTrigger className="w-20 bg-gray-800 border-gray-600 text-gray-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-600">
                        <SelectItem value="to" className="text-gray-200">To</SelectItem>
                        <SelectItem value="cc" className="text-gray-200">CC</SelectItem>
                        <SelectItem value="bcc" className="text-gray-200">BCC</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      value={customEmail}
                      onChange={(e) => setCustomEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="bg-gray-800 border-gray-600 text-gray-200"
                      onKeyDown={(e) => e.key === 'Enter' && handleCustomAdd()}
                    />
                    <Button onClick={handleCustomAdd} className="bg-blue-600 hover:bg-blue-700 shrink-0">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Inline save-as-contact popup (task #66: assign client + priority without extra clicks) */}
                  {showContactForm && (
                    <div className="mt-2 rounded border border-blue-800 bg-blue-950/40 p-3 space-y-2">
                      <p className="text-xs text-blue-300">New address — save it as a contact for this project:</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          value={newContact.firstName}
                          onChange={(e) => setNewContact(p => ({ ...p, firstName: e.target.value }))}
                          placeholder="First name"
                          className="bg-gray-800 border-gray-600 text-gray-200 h-8"
                        />
                        <Input
                          value={newContact.lastName}
                          onChange={(e) => setNewContact(p => ({ ...p, lastName: e.target.value }))}
                          placeholder="Last name"
                          className="bg-gray-800 border-gray-600 text-gray-200 h-8"
                        />
                        <Select value={newContact.clientId || 'none'} onValueChange={(v) => setNewContact(p => ({ ...p, clientId: v === 'none' ? '' : v }))}>
                          <SelectTrigger className="bg-gray-800 border-gray-600 text-gray-200 h-8">
                            <SelectValue placeholder="Client (optional)" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600">
                            <SelectItem value="none" className="text-gray-200">No client</SelectItem>
                            {clientsList?.map((c: any) => (
                              <SelectItem key={c.id} value={c.id} className="text-gray-200">{c.companyName || c.id}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={newContact.role} onValueChange={(v) => setNewContact(p => ({ ...p, role: v }))}>
                          <SelectTrigger className="bg-gray-800 border-gray-600 text-gray-200 h-8">
                            <SelectValue placeholder="Priority / role" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-600">
                            {PROJECT_ROLES.map(r => (
                              <SelectItem key={r.value} value={r.value} className="text-gray-200">{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {user?.role === 'admin' && (
                          <Select value={newContact.category || 'none'} onValueChange={(v) => setNewContact(p => ({ ...p, category: v === 'none' ? '' : v }))}>
                            <SelectTrigger className="bg-gray-800 border-gray-600 text-gray-200 h-8">
                              <SelectValue placeholder="Category (optional)" />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-600">
                              <SelectItem value="none" className="text-gray-200">No category</SelectItem>
                              {CONTACT_CATEGORIES.map(c => (
                                <SelectItem key={c} value={c} className="text-gray-200">{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          type="button" size="sm" variant="ghost"
                          className="text-gray-400"
                          onClick={() => {
                            addRecipientDirect(customEmail, undefined, customType);
                            setCustomEmail("");
                            setShowContactForm(false);
                          }}
                        >
                          Just add, don't save
                        </Button>
                        <Button
                          type="button" size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          disabled={saveContactMutation.isPending}
                          onClick={() => saveContactMutation.mutate()}
                        >
                          {saveContactMutation.isPending ? 'Saving…' : 'Save contact & add'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <Separator className="bg-gray-700" />

          {/* Subject */}
          <div>
            <Label className="text-gray-200 font-medium">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onBlur={() => setSubject(current => ensureSingleProjectId(current, projectId))}
              placeholder="Email subject..."
              className="bg-gray-800 border-gray-600 text-gray-200"
            />
            <p className="text-xs text-gray-500 mt-1">
              The project ID ({projectId}) is appended automatically so replies are assigned to this project.
            </p>
          </div>

          {/* Authoritative source preview / Rich Text Editor */}
          <div>
            {(isReplyMode || isForwardMode) && (
              <div
                className="mb-4 overflow-hidden rounded-md border border-slate-700 bg-slate-950/55"
                data-testid={isForwardMode ? "forward-original-email" : "reply-original-email"}
              >
                <div className="flex items-center justify-between gap-3 border-b border-slate-700 bg-slate-800/80 px-3 py-2">
                  <div>
                    <p className="text-xs font-semibold tracking-wide text-slate-200">Aus der Original-E-Mail:</p>
                    {resolvedSource && (
                      <p className="mt-0.5 text-xs text-slate-400">
                        {resolvedSource.fromName || resolvedSource.fromAddress} · {resolvedSource.emailDate ? new Date(resolvedSource.emailDate).toLocaleString() : 'Date unavailable'}
                      </p>
                    )}
                  </div>
                  {isForwardMode
                    ? <Forward className="h-4 w-4 shrink-0 text-blue-400" aria-hidden="true" />
                    : <Reply className="h-4 w-4 shrink-0 text-blue-400" aria-hidden="true" />}
                </div>
                {sourceLoading ? (
                  <div className="space-y-2 p-4" aria-label="Loading original email">
                    <div className="h-3 w-2/5 animate-pulse rounded bg-slate-800" />
                    <div className="h-3 w-full animate-pulse rounded bg-slate-800" />
                    <div className="h-3 w-4/5 animate-pulse rounded bg-slate-800" />
                  </div>
                ) : sourceError ? (
                  <div className="flex items-center justify-between gap-3 p-4 text-sm text-rose-300">
                    <span>
                      The original email could not be loaded. {isForwardMode ? 'Forwarding' : 'Sending'} is unavailable until it is restored.
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => isForwardMode ? refetchForwardSource() : refetchReplySource()}
                      className="border-slate-600 text-slate-200"
                    >
                      Retry
                    </Button>
                  </div>
                ) : (
                  <div
                    className="max-h-56 select-text overflow-y-auto px-4 py-3 text-sm leading-6 text-slate-300 prose prose-sm prose-invert max-w-none"
                    style={{ userSelect: 'text' }}
                    onCopy={handleOriginalEmailCopy}
                    dangerouslySetInnerHTML={{ __html: renderOriginalEmailBody(resolvedSource?.emailBody || null) }}
                  />
                )}
                {!sourceLoading && !sourceError && (
                  <p className="border-t border-slate-800 px-4 py-2 text-[11px] text-slate-500">
                    Markierte Passagen werden beim Kopieren automatisch als Zitat gekennzeichnet.
                  </p>
                )}
              </div>
            )}
            <Label className="text-gray-200 font-medium mb-2 block">
              Email Content
            </Label>
            <div
              onDrop={handleImageDrop}
              onDragOver={(e) => e.preventDefault()}
              className="bg-gray-800 border border-gray-600 rounded-md"
            >
              <ReactQuill
                useSemanticHTML={false}
                ref={quillRef}
                theme="snow"
                value={htmlContent}
                onChange={setHtmlContent}
                modules={quillModules}
                style={{ backgroundColor: '#1f2937', color: '#f3f4f6', minHeight: '300px' }}
                className="rich-text-dark"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Drag and drop images directly into the editor or use the image button in the toolbar
            </p>
          </div>

          {isShareDraft && (
            <ShareEmailOverviewEditor
              overview={overviewQuery.data}
              excludedIds={shareEmailExcludedIds}
              onExcludedIdsChange={setShareEmailExcludedIds}
              mode={shareEmailThumbnailMode}
              onModeChange={setShareEmailThumbnailMode}
              loading={overviewQuery.isLoading}
              error={overviewQuery.isError}
              onRetry={() => { void overviewQuery.refetch(); }}
            />
          )}

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-gray-200 font-medium">Attachments</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Paperclip className="w-4 h-4 mr-1" />
                Add Files
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept="*/*"
              />
            </div>

            {isForwardMode && resolvedForwardSource && (
              <div className="mb-4 rounded-md border border-slate-700 bg-slate-950/40 p-3" data-testid="forward-source-attachments">
                <p className="text-sm font-medium text-slate-200">Original attachments</p>
                {(resolvedForwardSource.attachments || []).length === 0 ? (
                  <p className="mt-1 text-xs text-slate-400">The original email has no attachments.</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {resolvedForwardSource.attachments.map(attachment => {
                      const selected = selectedForwardAttachmentIds.includes(attachment.id);
                      return (
                        <label
                          key={attachment.id}
                          className={`flex items-start gap-2 rounded px-2 py-1.5 text-sm ${
                            attachment.available ? 'cursor-pointer hover:bg-slate-800' : 'cursor-not-allowed bg-amber-950/25'
                          }`}
                        >
                          <Checkbox
                            checked={selected}
                            disabled={!attachment.available}
                            onCheckedChange={(checked) => {
                              if (!attachment.available) return;
                              setSelectedForwardAttachmentIds(current => checked
                                ? [...new Set([...current, attachment.id])]
                                : current.filter(id => id !== attachment.id),
                              );
                            }}
                            aria-label={`Include original attachment ${attachment.fileName}`}
                          />
                          <span className="min-w-0 flex-1 text-slate-200">
                            <span className="block truncate">{attachment.fileName}</span>
                            <span className="block text-xs text-slate-400">
                              {attachment.available
                                ? `${attachment.contentType}${attachment.size === null ? '' : ` · ${(attachment.size / 1024).toFixed(1)} KB`}`
                                : `Will not be included${attachment.unavailableReason ? `: ${attachment.unavailableReason}` : ' because this file is unavailable.'}`}
                            </span>
                          </span>
                          {!attachment.available && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {isForwardMode && (
              <div
                className={`mb-3 rounded-md border px-3 py-2 text-xs ${
                  forwardAttachmentLimitError
                    ? 'border-rose-800 bg-rose-950/50 text-rose-200'
                    : 'border-slate-700 bg-slate-900/50 text-slate-400'
                }`}
                data-testid="forward-attachment-limits"
              >
                <span>
                  Original and new attachments combined: {combinedForwardAttachmentCount}/{FORWARD_COMBINED_ATTACHMENT_MAX_COUNT} files,{' '}
                  {(combinedKnownForwardAttachmentBytes / (1024 * 1024)).toFixed(1)} / 25 MB known size.
                </span>
                {selectedForwardAttachments.some(attachment => attachment.size === null) && (
                  <span> Files with an unknown original size are checked by the server before sending.</span>
                )}
                {forwardAttachmentLimitError && <span className="block mt-1 font-medium">{forwardAttachmentLimitError}</span>}
              </div>
            )}

            {attachments.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {attachments.map((attachment, index) => (
                  <div key={index} className="bg-gray-800 border border-gray-600 rounded-md p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-200 truncate">
                        {attachment.name}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {attachment.preview && (
                      <img
                        src={attachment.preview}
                        alt={attachment.name}
                        className="w-full h-20 object-cover rounded"
                      />
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {(attachment.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator className="bg-gray-700" />

          {deliveryRequestConflict && (
            <div role="alert" data-testid="delivery-request-conflict" className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
              <p className="font-semibold">Sending blocked — existing delivery conflict</p>
              <p className="mt-1">{deliveryRequestConflict}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                disabled={sendEmailMutation.isPending || saveDraft.isPending}
                onClick={() => onOpenChange(false)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Close
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={sendDisabled}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sendEmailMutation.isPending ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
          </div>
        </div>
      </DialogContent>
      <AlertDialog
        open={fromNameConfirmationOpen}
        onOpenChange={(open) => {
          setFromNameConfirmationOpen(open);
          if (!open) setPendingSendHtml(null);
        }}
      >
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Change the global From Name?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300 space-y-3">
              <span className="block">
                This changes the sender name recipients see on this email and all future PM emails.
              </span>
              <span className="block rounded-md border border-gray-700 bg-gray-800 p-3">
                <span className="block text-gray-400">Current: {emailStandards?.fromName || 'Acolori • Team Update'}</span>
                <span className="block font-medium text-white">New: {fromName.trim()}</span>
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep current name</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmFromNameChange}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              Confirm change and send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={shareReadinessOpen && !!shareReadiness}
        // The readiness choice is explicit. The cancel action below is the
        // only way to close this nested modal; the draft underneath remains
        // open and editable.
        onOpenChange={(open) => {
          if (open) setShareReadinessOpen(true);
        }}
      >
        <AlertDialogContent
          className="z-[60] max-w-2xl border-amber-700 bg-gray-950 p-6 text-white shadow-2xl"
          data-testid="share-readiness-modal"
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          {shareReadiness && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-start gap-3 text-lg font-semibold">
                  <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-400" />
                  <span>Files are still being prepared</span>
                </AlertDialogTitle>
                <AlertDialogDescription className="mt-1 text-sm text-gray-300">
                  The share link is saved, but sending now would use files that may not be ready yet.
                  Keep this composer open to watch each file, or choose an explicit send action.
                </AlertDialogDescription>
              </AlertDialogHeader>

              <div className="mt-4 max-h-64 overflow-y-auto rounded-md border border-gray-700 bg-gray-900/70" data-testid="share-readiness-files">
                {shareReadiness.files.map((file) => (
                  <div key={file.id} className="flex items-center gap-3 border-b border-gray-800 px-3 py-2 last:border-b-0">
                    <div className={`h-2.5 w-2.5 shrink-0 rounded-full ${file.ready ? "bg-emerald-400" : "bg-amber-400"}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-gray-100">{file.fileName}</p>
                      <p className="text-xs text-gray-400">{file.detail}</p>
                    </div>
                    <div className="shrink-0 text-right text-[11px] text-gray-500">
                      <div>{file.transferStatus}</div>
                      {file.previewStatus && <div>{file.previewStatus}</div>}
                    </div>
                  </div>
                ))}
              </div>
              {!shareReadiness.ready && shareReadiness.reason && (
                <p className="mt-2 text-xs text-amber-300">{shareReadiness.reason}</p>
              )}
              {shareReadinessPollExpired && (
                <p className="mt-2 text-xs text-gray-400" data-testid="share-readiness-poll-expired">
                  Live progress paused after five minutes. Choose an action to continue; the composer and your draft are unchanged.
                </p>
              )}

              <AlertDialogFooter className="mt-5 flex-wrap gap-2">
                <AlertDialogCancel
                  type="button"
                  onClick={() => setShareReadinessOpen(false)}
                  className="border-gray-600 text-gray-300"
                  data-testid="share-readiness-cancel"
                >
                  Cancel Sending
                </AlertDialogCancel>
                <Button
                  type="button"
                  variant="outline"
                  disabled={sendEmailMutation.isPending}
                  onClick={() => handleShareReadinessAction("when_ready")}
                  className="border-blue-600 text-blue-200 hover:bg-blue-950"
                  data-testid="share-readiness-when-ready"
                >
                  Send when ready
                </Button>
                <Button
                  type="button"
                  disabled={sendEmailMutation.isPending}
                  onClick={() => handleShareReadinessAction("force")}
                  className="bg-amber-600 text-white hover:bg-amber-500"
                  data-testid="share-readiness-force"
                >
                  Send NOW!
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={thumbnailChoiceOpen} onOpenChange={setThumbnailChoiceOpen}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-100">Email thumbnails are still preparing</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Keep writing while the tiny images are prepared, or use a file-name overview without images.
              Nothing has been sent. After choosing, click Send Email when you are ready.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setThumbnailChoiceOpen(false)}>Wait and keep writing</AlertDialogCancel>
            <Button onClick={() => { setShareEmailThumbnailMode('without'); setThumbnailChoiceOpen(false); }}>
              Continue without thumbnails
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-100">Discard this composition?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This clears the current session, attachments and saved draft. Any previous composition kept while browsing history is also cleared.
              {deliveryRequestConflict && ' Starting fresh does not cancel an existing delivery. Check its status before sending another email.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-600 text-gray-300">Keep editing</AlertDialogCancel>
            <Button type="button" variant="destructive" onClick={async () => {
              if (!await removeSavedDraft()) return;
              resetForm();
              setDiscardOpen(false);
              onOpenChange(false);
            }}>Discard and close</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
